import React, { useState, useRef, useEffect, useCallback } from "react";
import Select, { StylesConfig, GroupBase } from "react-select";
import styled from "styled-components";
import { useTheme } from "styled-components";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Svg12 as ChevronDown } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDownFilled";

export interface SelectOption {
    value: string | number;
    label: string;
}

interface ReactSelectComponentProps {
    value?: SelectOption | null;
    onChange: (option: SelectOption | null) => void;
    options: SelectOption[];
    placeholder?: string;
    label?: string;
    name?: string;
    disabled?: boolean;
    error?: boolean;
    errorMessage?: string;
    isClearable?: boolean;
    isSearchable?: boolean;
    isLoading?: boolean;
}

export const ReactSelect: React.FC<ReactSelectComponentProps> = ({
    value,
    onChange,
    options,
    placeholder = "Select an option",
    label,
    name,
    disabled = false,
    error = false,
    errorMessage,
    isClearable = false,
    isSearchable = true,
    isLoading = false,
}) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const prevIsOpenRef = useRef(false);

    // Get the display value: show search text when menu is open and searching, or selected value when closed
    const displayValue = React.useMemo(() => {
        // When menu is open and searchable, always show searchText (even if empty)
        // This allows clearing the search without reverting to selected value
        if (isOpen && isSearchable) {
            return searchText;
        }
        // When menu is closed, show selected value
        return value?.label || "";
    }, [isOpen, isSearchable, searchText, value]);

    // Filter options based on search text
    const filteredOptions = React.useMemo(() => {
        if (!isSearchable || !searchText.trim()) {
            return options;
        }
        const searchLower = searchText.toLowerCase();
        return options.filter((option) => option.label.toLowerCase().includes(searchLower));
    }, [options, searchText, isSearchable]);

    // Helper function to blur input and dismiss keyboard on mobile
    const blurInput = useCallback(() => {
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        if (containerRef.current) {
            const input = containerRef.current.querySelector("input");
            if (input) {
                input.blur();
            }
        }
    }, []);

    // Clear search text when menu opens (transitions from closed to open)
    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setSearchText("");
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchText(""); // Clear search when closing
                blurInput(); // Dismiss keyboard on mobile
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, blurInput]);

    const handleTextFieldFocus = () => {
        if (!disabled) {
            setIsOpen(true);
        }
    };

    const handleTextFieldClick = () => {
        if (!disabled) {
            setIsOpen(true);
        }
    };

    const handleTextFieldChange = (value: string) => {
        if (isSearchable) {
            // If clearing the search (empty string), show selected value
            if (value === "") {
                setSearchText("");
                setIsOpen(true); // Keep menu open to show all options
            } else {
                setSearchText(value);
                setIsOpen(true); // Keep menu open when typing
            }
        }
    };

    const handleSelectChange = (option: SelectOption | null) => {
        onChange(option);
        setIsOpen(false);
        setSearchText(""); // Clear search after selection
        blurInput(); // Dismiss keyboard on mobile
    };

    const selectStyles: StylesConfig<SelectOption, false, GroupBase<SelectOption>> = {
        control: () => ({
            border: "none",
            boxShadow: "none",
            display: "flex",
            minHeight: "auto",
        }),
        menu: (baseStyles: any) => ({
            ...baseStyles,
            borderRadius: theme.spacing.medium,
            backgroundColor: theme.colors[theme.colorMode].background_Primary,
            boxShadow: "none",
            marginTop: 0,
            marginBottom: 0,
        }),
        menuList: (baseStyles: any) => ({
            ...baseStyles,
            padding: theme.spacing.xSmall,
            maxHeight: "300px",
            borderRadius: theme.spacing.medium,
            border: `1px solid ${theme.colors[theme.colorMode].border_Secondary}`,
        }),
        option: (baseStyles: any, state: any) => ({
            ...baseStyles,
            fontSize: "16px",
            fontWeight: 500,
            backgroundColor: state.isSelected
                ? theme.colors[theme.colorMode].primary
                : state.isFocused
                ? theme.colors[theme.colorMode].background_Secondary
                : "transparent",
            color: state.isSelected
                ? theme.colors[theme.colorMode].text_Button_Primary
                : theme.colors[theme.colorMode].text_High_Emphasis,
            borderRadius: theme.spacing.xSmall,
            padding: theme.spacing.small,
            cursor: "pointer",
            "&:active": {
                backgroundColor: theme.colors[theme.colorMode].primary,
                color: theme.colors[theme.colorMode].text_Button_Primary,
            },
        }),
        indicatorSeparator: () => ({
            display: "none",
        }),
        dropdownIndicator: () => ({
            display: "none",
        }),
        input: () => ({
            display: "none", // Hide react-select's default input
        }),
        valueContainer: () => ({
            display: "none", // Hide value container since we're not showing selected value here
        }),
    };

    // Custom Input component to hide react-select's default search input
    const CustomInput = () => null;

    const DropdownIndicator = () => null;

    return (
        <SelectContainer ref={containerRef}>
            <TextFieldWrapper onFocus={handleTextFieldFocus} onClick={handleTextFieldClick}>
                <TextField
                    textFieldDetails={{
                        value: displayValue,
                        label: label || "",
                        name: name || "select",
                        type: TextFieldTypes.TEXT,
                        onChange: handleTextFieldChange,
                        isDisabled: disabled || !isSearchable,
                        errorText: error ? errorMessage : "",
                        attrs: {
                            autoComplete: "off",
                        },
                    }}
                />
                <ChevronIcon hasError={error}>
                    <GenericIcon Icon={ChevronDown} fillColor="text_Highest_Emphasis" />
                </ChevronIcon>
            </TextFieldWrapper>
            {isOpen && (
                <>
                    <Blanket
                        onClick={() => {
                            setIsOpen(false);
                            blurInput(); // Dismiss keyboard on mobile
                        }}
                    />
                    <Menu>
                        <SelectWrapper>
                            <Select
                                backspaceRemovesValue={false}
                                components={{
                                    Input: CustomInput, // Hide default input
                                    DropdownIndicator,
                                    IndicatorSeparator: () => null,
                                }}
                                controlShouldRenderValue={false}
                                hideSelectedOptions={false}
                                isClearable={isClearable}
                                isSearchable={false} // Disable react-select's built-in search
                                isLoading={isLoading}
                                menuIsOpen
                                onChange={(newValue: any) => handleSelectChange(newValue as SelectOption | null)}
                                options={filteredOptions}
                                placeholder={placeholder}
                                styles={selectStyles}
                                tabSelectsValue={false}
                                value={value}
                                menuPlacement="auto"
                                captureMenuScroll={false}
                            />
                        </SelectWrapper>
                    </Menu>
                </>
            )}
        </SelectContainer>
    );
};

const SelectContainer = styled.div`
    position: relative;
    width: 100%;
`;

const TextFieldWrapper = styled.div`
    position: relative;
    width: 100%;
    cursor: pointer;

    input:focus {
        outline: none;
    }

    /* Make the input clickable even when disabled */
    input {
        cursor: pointer;
    }
`;

const ChevronIcon = styled.div<{ hasError: boolean }>`
    position: absolute;
    right: ${({ theme }) => theme.spacing.medium};
    top: ${({ hasError }) => (hasError ? "32%" : "50%")};
    transform: translateY(-50%);
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
`;

const Menu = styled.div(({ theme }) => ({
    backgroundColor: theme.colors[theme.colorMode].background_Primary,
    borderRadius: theme.spacing.medium,
    border: "none",
    boxShadow: `0 4px 12px rgba(0, 0, 0, 0.1)`,
    marginTop: theme.spacing.xSmall,
    position: "absolute",
    width: "100%",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
}));

const SelectWrapper = styled.div`
    /* Ensure react-select renders correctly */
    & > div {
        border: none;
        box-shadow: none;
    }

    /* Remove border from react-select menu */
    & div[class*="menu"] {
        border: none !important;
    }
`;

const Blanket = styled.div`
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1;
`;
